import { useCallback, useEffect, useState, useMemo, Fragment } from "react";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/format";
import { useCurrencyRail } from "@/context/CurrencyRailContext";
import { useEntity } from "@/context/EntityContext";
import ConsolidatedRailCard from "@/components/ConsolidatedRailCard";
import DrillDownModal from "@/components/DrillDownModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  SelectGroup, SelectLabel,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  TrendingDown, TrendingUp, Scale, BookOpen, FileSpreadsheet,
  Download, Plus, Trash2, ArrowDownUp, Sparkles,
  CheckSquare, Square, ChevronDown, Building2, Tag, X, Users,
} from "lucide-react";
import { startOfMonth, endOfMonth, startOfYear, endOfYear } from "date-fns";
import { toast } from "sonner";
import { DateField, fmtISO, downloadStream, CsvButton, BalancedBadge, RailSectionHeader } from "./_shared";

export function PnLReport({ currency }) {
  const [start, setStart] = useState(startOfMonth(new Date()));
  const [end, setEnd] = useState(endOfMonth(new Date()));
  const [rawData, setRawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null); // {title, params}
  const { filterRails } = useCurrencyRail();
  const { currentEntity } = useEntity();
  const isBusiness = currentEntity?.workspace_type === "business";
  // iter 72z — Business workspaces can toggle P&L aggregation between
  // "auto" (CoA-preferred, category fallback) and "department" (spend
  // per business function — always the tx `category` which is the
  // Department in Business). Personal keeps the auto/category behavior.
  const [groupBy, setGroupBy] = useState("auto");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounting/pnl", {
        params: { start: fmtISO(start), end: fmtISO(end), group_by: groupBy },
      });
      setRawData(data);
    } finally { setLoading(false); }
  }, [start, end, groupBy]);
  useEffect(() => { load(); }, [load]);
  // Filter by the sidebar-selected rail. When user picks USD/NGN, only that rail's data flows through.
  const data = rawData ? { ...rawData, by_currency: filterRails(rawData.by_currency || []) } : null;

  const quick = (label, s, e) => (
    <Button key={label} variant="outline" size="sm"
      data-testid={`pnl-quick-${label.toLowerCase().replace(/\s/g, "-")}`}
      onClick={() => { setStart(s); setEnd(e); }} className="h-8 text-xs">{label}</Button>
  );

  return (
    <div data-testid="pnl-report" className="space-y-6">
      <div className="flex flex-wrap items-end gap-3 justify-between">
        <div className="flex gap-3 items-end">
          <DateField label="From" value={start} onChange={setStart} testId="pnl-start" />
          <DateField label="To" value={end} onChange={setEnd} testId="pnl-end" />
          {isBusiness && (
            <div className="space-y-1" data-testid="pnl-group-by-field">
              <Label className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Group by</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger className="h-9 w-40" data-testid="pnl-group-by">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto" data-testid="pnl-group-by-auto">
                    <span className="inline-flex items-center gap-2"><Tag className="w-3.5 h-3.5" /> Auto (CoA)</span>
                  </SelectItem>
                  <SelectItem value="department" data-testid="pnl-group-by-department">
                    <span className="inline-flex items-center gap-2"><Users className="w-3.5 h-3.5" /> Department</span>
                  </SelectItem>
                  <SelectItem value="coa" data-testid="pnl-group-by-coa">
                    <span className="inline-flex items-center gap-2"><Building2 className="w-3.5 h-3.5" /> Chart of Accounts</span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <div className="flex gap-1.5 items-center">
          {quick("This month", startOfMonth(new Date()), endOfMonth(new Date()))}
          {quick("Last month", startOfMonth(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)),
                              endOfMonth(new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1)))}
          {quick("YTD", startOfYear(new Date()), new Date())}
          {quick("This year", startOfYear(new Date()), endOfYear(new Date()))}
          <CsvButton testId="pnl-csv" onClick={() => downloadStream("/accounting/pnl.csv",
            { start: fmtISO(start), end: fmtISO(end), group_by: groupBy }, `pnl_${fmtISO(start)}_${fmtISO(end)}.csv`)} />
        </div>
      </div>

      {loading && <div className="text-sm text-[var(--muted-text)]">Loading…</div>}
      {data && (
        <>
          {Array.isArray(data.by_currency) && data.by_currency.length > 1 ? (
            /* Multi-currency: show per-currency KPI stacks — no summing across rails */
            <div data-testid="pnl-kpi-multi" className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.by_currency.map((r) => (
                <div key={r.currency} data-testid={`pnl-kpi-${r.currency}`} className="card-flat p-5 border-l-4"
                     style={{ borderLeftColor: r.currency === "USD" ? "#1E5A96" : "#4C956C" }}>
                  <div className="flex items-baseline justify-between">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">{r.currency} rail</div>
                    <div className={`text-xs font-mono-data ${r.net_income >= 0 ? "text-[#2F6B4A]" : "text-[#B4283A]"}`}>
                      Net {formatCurrency(r.net_income, r.currency)}
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] flex items-center gap-1.5">
                        <TrendingUp className="w-3 h-3 text-[#4C956C]" /> Revenue
                      </div>
                      <div data-testid={`pnl-total-income-${r.currency}`} className="font-mono-data text-xl mt-1 text-[#4C956C]">
                        {formatCurrency(r.income.total, r.currency)}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] flex items-center gap-1.5">
                        <TrendingDown className="w-3 h-3 text-[#E63946]" /> Expenses
                      </div>
                      <div data-testid={`pnl-total-expense-${r.currency}`} className="font-mono-data text-xl mt-1 text-[#E63946]">
                        {formatCurrency(r.expenses.total, r.currency)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Single-currency: original mixed cards */
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="card-flat p-5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] flex items-center gap-2">
                  <TrendingUp className="w-3.5 h-3.5 text-[#4C956C]" /> Revenue
                </div>
                <div data-testid="pnl-total-income" className="font-mono-data text-2xl mt-2 text-[#4C956C]">
                  {formatCurrency(data.income.total, data?.currency || currency)}
                </div>
              </div>
              <div className="card-flat p-5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] flex items-center gap-2">
                  <TrendingDown className="w-3.5 h-3.5 text-[#E63946]" /> Expenses
                </div>
                <div data-testid="pnl-total-expense" className="font-mono-data text-2xl mt-2 text-[#E63946]">
                  {formatCurrency(data.expenses.total, data?.currency || currency)}
                </div>
              </div>
              <div className="card-flat p-5">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Net income</div>
                <div data-testid="pnl-net-income" className={`font-mono-data text-2xl mt-2 ${data.net_income >= 0 ? "text-[#2F4F4F]" : "text-[#E63946]"}`}>
                  {formatCurrency(data.net_income, data?.currency || currency)}
                </div>
              </div>
            </div>
          )}

          {/* Per-currency breakdown — surfaces mixed-USD/NGN books cleanly */}
          {Array.isArray(data.by_currency) && data.by_currency.length > 1 && (
            <ConsolidatedRailCard
              testId="pnl-consolidated"
              subtitle="flip the sidebar FX toggle to hide"
              rails={data.by_currency.map((r) => ({
                currency: r.currency,
                revenue: r.income.total,
                expenses: r.expenses.total,
                net_income: r.net_income,
              }))}
              fields={[
                { key: "revenue", label: "Revenue", color: "text-[#4C956C]" },
                { key: "expenses", label: "Expenses", color: "text-[#E63946]" },
                { key: "net_income", label: "Net income" },
              ]}
            />
          )}
          {Array.isArray(data.by_currency) && data.by_currency.length > 1 && (
            <div className="card-flat overflow-hidden" data-testid="pnl-by-currency">
              <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)] flex items-center gap-2">
                Per-currency breakdown
                <span className="text-[10px] normal-case tracking-normal text-[var(--muted-text)]">· each rail is native — no FX cross-mixing</span>
              </div>
              <table className="qb-table w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-[var(--muted-text)] border-b border-[var(--divider)]">
                    <th className="px-5 py-2 font-medium">Currency</th>
                    <th className="px-5 py-2 font-medium text-right">Revenue</th>
                    <th className="px-5 py-2 font-medium text-right">Expenses</th>
                    <th className="px-5 py-2 font-medium text-right">Net income</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_currency.map((r) => (
                    <tr key={r.currency} data-testid={`pnl-ccy-row-${r.currency}`} className="border-b border-[var(--divider)] last:border-none">
                      <td className="px-5 py-2.5 font-mono-data text-xs">{r.currency}</td>
                      <td className="px-5 py-2.5 text-right font-mono-data text-[#4C956C]">{formatCurrency(r.income.total, r.currency)}</td>
                      <td className="px-5 py-2.5 text-right font-mono-data text-[#E63946]">{formatCurrency(r.expenses.total, r.currency)}</td>
                      <td className={`px-5 py-2.5 text-right font-mono-data font-medium ${r.net_income >= 0 ? "text-[#2F4F4F]" : "text-[#E63946]"}`}>
                        {formatCurrency(r.net_income, r.currency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(!Array.isArray(data.by_currency) || data.by_currency.length <= 1) && (
            <>
              {[
                { title: "Income", key: "income", drillKind: "income", testPrefix: "pnl-income-row" },
                { title: "Expenses", key: "expenses", drillKind: "expense", testPrefix: "pnl-expense-row" },
              ].map((sec) => (
                <div key={sec.key} className="card-flat overflow-hidden">
                  <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">{sec.title}</div>
                  {data[sec.key].lines.length === 0 ? (
                    <div className="p-5 text-sm text-[var(--muted-text)]">No {sec.title.toLowerCase()} in this period.</div>
                  ) : (
                    <table className="qb-table w-full text-sm">
                      <tbody>
                        {data[sec.key].lines.map((l) => (
                          <tr
                            key={l.category}
                            data-testid={`${sec.testPrefix}-${l.category}`}
                            onClick={() => setDrill({
                              title: `${l.category} · ${formatCurrency(l.amount, data?.currency || currency)}`,
                              params: { kind: sec.drillKind, category: l.category,
                                        start: fmtISO(start), end: fmtISO(end) },
                            })}
                            className="cursor-pointer border-b border-[var(--divider)] last:border-none"
                            title="Click to drill down"
                          >
                            <td className="py-2.5 px-5 flex items-center gap-2">
                              <span>{l.category}</span>
                              <span className="text-[10px] text-[var(--muted-text)] opacity-0 group-hover:opacity-100 transition-opacity">→</span>
                            </td>
                            <td className="py-2.5 px-5 text-right font-mono-data">{formatCurrency(l.amount, data?.currency || currency)}</td>
                          </tr>
                        ))}
                        <tr className="bg-[var(--hover)] font-medium">
                          <td className="py-2.5 px-5">Total {sec.title.toLowerCase()}</td>
                          <td className="py-2.5 px-5 text-right font-mono-data">{formatCurrency(data[sec.key].total, data?.currency || currency)}</td>
                        </tr>
                      </tbody>
                    </table>
                  )}
                </div>
              ))}

              <div className="card-flat p-5 border-l-4 border-l-[#2F4F4F]">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)]">Net income for period</div>
                    <div className="text-xs text-[var(--muted-text)] mt-1">Revenue minus expenses. Savings are not a P&amp;L expense — they&apos;re a balance sheet reclassification.</div>
                  </div>
                  <div className={`font-mono-data text-3xl ${data.net_income >= 0 ? "text-[#2F4F4F]" : "text-[#E63946]"}`}>
                    {formatCurrency(data.net_income, data?.currency || currency)}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Multi-currency: render per-currency Income/Expense line tables — each in native currency, no cross-mixing */}
          {Array.isArray(data.by_currency) && data.by_currency.length > 1 && data.by_currency.map((rail) => (
            <div key={`rail-${rail.currency}`} data-testid={`pnl-rail-${rail.currency}`}>
              <RailSectionHeader
                currency={rail.currency}
                subtitle={`Revenue − Expenses = ${formatCurrency(rail.net_income, rail.currency)}`}
                testId={`pnl-rail-header-${rail.currency}`}
                right={
                  <div className={`font-mono-data text-sm ${rail.net_income >= 0 ? "text-[#2F4F4F]" : "text-[#E63946]"}`}>
                    Net {formatCurrency(rail.net_income, rail.currency)}
                  </div>
                }
              />
              <div className="card-flat overflow-hidden mt-2">
                {[
                  { title: "Income", key: "income", color: "text-[#4C956C]" },
                  { title: "Expenses", key: "expenses", color: "text-[#E63946]" },
                ].map((sec) => (
                  <div key={sec.key}>
                    <div className="px-5 py-2 border-b border-[var(--divider)] text-[10px] tracking-[0.2em] uppercase text-[var(--muted-text)] bg-[var(--hover)]">{sec.title}</div>
                    {rail[sec.key].lines.length === 0 ? (
                      <div className="px-5 py-3 text-xs text-[var(--muted-text)]">No {sec.title.toLowerCase()} in this period.</div>
                    ) : (
                      <table className="qb-table w-full text-sm">
                        <tbody>
                          {rail[sec.key].lines.map((l) => (
                            <tr key={`${rail.currency}-${sec.key}-${l.category}`}
                                data-testid={`pnl-${sec.key}-${rail.currency}-${l.category}`}
                                className="border-b border-[var(--divider)] last:border-none">
                              <td className="py-2 px-5">{l.category}</td>
                              <td className={`py-2 px-5 text-right font-mono-data ${sec.color}`}>
                                {formatCurrency(l.amount, rail.currency)}
                              </td>
                            </tr>
                          ))}
                          <tr className="bg-[var(--hover)] font-medium">
                            <td className="py-2 px-5">Total {sec.title.toLowerCase()}</td>
                            <td className={`py-2 px-5 text-right font-mono-data ${sec.color}`}>
                              {formatCurrency(rail[sec.key].total, rail.currency)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="card-flat overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--divider)] text-xs tracking-[0.2em] uppercase text-[var(--muted-text)]">
              Appropriated to savings (informational)
            </div>
            {data.savings_appropriated.lines.length === 0 ? (
              <div className="px-5 py-3 text-sm text-[var(--muted-text)]">No amounts set aside to savings in this period.</div>
            ) : (
              <table className="qb-table w-full text-sm">
                <tbody>
                  {data.savings_appropriated.lines.map((l) => (
                    <tr
                      key={l.category}
                      data-testid={`pnl-saving-row-${l.category}`}
                      onClick={() => setDrill({
                        title: `Savings — ${l.category} · ${formatCurrency(l.amount, data?.currency || currency)}`,
                        params: { kind: "saving", category: l.category,
                                  start: fmtISO(start), end: fmtISO(end) },
                      })}
                      className="cursor-pointer border-b border-[var(--divider)] last:border-none"
                    >
                      <td className="py-2.5 px-5">{l.category}</td>
                      <td className="py-2.5 px-5 text-right font-mono-data text-[var(--muted-text)]">{formatCurrency(l.amount, data?.currency || currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="border-t border-[var(--divider)] bg-[var(--hover)] px-5 py-3 flex items-baseline justify-between text-sm">
              <span className="font-medium">Retained (net income − savings)</span>
              <span data-testid="pnl-retained" className="font-mono-data font-semibold">
                {formatCurrency(data.retained_after_savings, data?.currency || currency)}
              </span>
            </div>
          </div>
        </>
      )}
      <DrillDownModal
        open={!!drill}
        onClose={() => setDrill(null)}
        title={drill?.title}
        params={drill?.params}
        currency={currency}
      />
    </div>
  );
}
